import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { notify } from "@/lib/toast";

const EMPTY_FORM = { name: "", business: "", phone: "", email: "", password: "", promo_code: "" };

export default function Register() {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [promoStatus, setPromoStatus] = useState(null); // null | "ok" | "bad"
    const [showPassword, setShowPassword] = useState(false);

    function onChange(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
        if (k === "promo_code") setPromoStatus(null);
    }

    async function validatePromo() {
        const code = form.promo_code.trim();
        if (!code) { setPromoStatus(null); return; }
        try {
            await api.post("/promo/validate", { code });
            setPromoStatus("ok");
            notify("Promo applied: 30 days FREE on Growth", "success");
        } catch {
            setPromoStatus("bad");
        }
    }

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/register", {
                name: form.name.trim(),
                business: form.business.trim(),
                phone: form.phone,
                email: form.email.trim(),
                password: form.password,
                promo_code: form.promo_code.trim(),
            });
            await refresh();
            notify("Account created!");
            nav("/qr");
        } catch (err) {
            notify(formatErr(err), "error");
        } finally {
            setLoading(false);
        }
    }

    return (
        <section className="min-h-screen flex items-center justify-center pt-40 pb-16 relative mesh-gradient" data-testid="register-page">
            <div className="max-w-md w-full mx-4">
                <RegisterHeader />
                <div className="glass rounded-3xl p-8">
                    <form onSubmit={onSubmit} className="space-y-5">
                        <RegisterFields
                            form={form} onChange={onChange}
                            showPassword={showPassword} setShowPassword={setShowPassword}
                        />
                        <PromoField
                            value={form.promo_code}
                            onChange={(v) => onChange("promo_code", v)}
                            onBlur={validatePromo}
                            status={promoStatus}
                        />
                        <button data-testid="reg-submit" type="submit" disabled={loading}
                            className="btn-primary w-full py-4 rounded-xl text-lg font-semibold mt-4 disabled:opacity-50">
                            {loading ? "Creating..." : "Create Account & Generate QR"}
                        </button>
                    </form>
                    <div className="mt-6 text-center text-sm text-gray-500">
                        Already have an account? <Link to="/login" className="text-brand-400 hover:underline">Login here</Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function RegisterHeader() {
    return (
        <div className="text-center mb-10">
            <h1 className="font-display font-bold text-3xl mb-2">Create Your Account</h1>
            <p className="text-gray-400">Start generating WhatsApp leads in minutes</p>
        </div>
    );
}

function RegisterFields({ form, onChange, showPassword, setShowPassword }) {
    return (
        <>
            <Field label="Full Name *">
                <input data-testid="reg-name" required value={form.name} onChange={(e) => onChange("name", e.target.value)}
                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="John Doe" />
            </Field>
            <Field label="Business Name">
                <input data-testid="reg-business" value={form.business} onChange={(e) => onChange("business", e.target.value)}
                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="Acme Corp (optional)" />
            </Field>
            <Field label="WhatsApp Number * (with country code)">
                <input data-testid="reg-phone" type="tel" required value={form.phone} onChange={(e) => onChange("phone", e.target.value)}
                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="e.g., 919876543210" />
            </Field>
            <Field label="Email">
                <input data-testid="reg-email" type="email" value={form.email} onChange={(e) => onChange("email", e.target.value)}
                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="john@example.com (optional)" />
            </Field>
            <Field label="Password *">
                <PasswordInput
                    value={form.password}
                    onChange={(v) => onChange("password", v)}
                    show={showPassword}
                    onToggle={() => setShowPassword((s) => !s)}
                />
            </Field>
        </>
    );
}

function PromoField({ value, onChange, onBlur, status }) {
    const borderClass = status === "ok" ? "border-brand-400" : status === "bad" ? "border-red-500" : "";
    return (
        <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center justify-between">
                <span>Promo Code <span className="text-gray-500">(optional)</span></span>
                {status === "ok" && <span className="text-brand-400 text-xs">✓ 30 days FREE on Growth</span>}
                {status === "bad" && <span className="text-red-400 text-xs">Invalid code</span>}
            </label>
            <input data-testid="reg-promo" value={value}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                onBlur={onBlur}
                className={`input-field w-full px-4 py-3 rounded-xl uppercase tracking-wider ${borderClass}`}
                placeholder="e.g., CONNEXA03" />
        </div>
    );
}

function PasswordInput({ value, onChange, show, onToggle }) {
    return (
        <div className="relative">
            <input data-testid="reg-password" type={show ? "text" : "password"} required minLength={6}
                value={value} onChange={(e) => onChange(e.target.value)}
                className="input-field w-full px-4 py-3 pr-12 rounded-xl" placeholder="Min 6 characters" />
            <button type="button" onClick={onToggle}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-brand-400 transition-colors"
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="reg-toggle-password">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
            {children}
        </div>
    );
}
